import type { IProductConstructorParams } from "@services/sdk";
import type { ID } from "@services/sdk/types";
import type { Moment } from "moment";
import { map } from "ramda";

import { ProductFamilyEnum, mapStringProductFamily } from "./product-family";
import ProductInfo from "./product-info";
import ProductItem from "./product-item";
import { ProductTypeEnum, mapStringProductType } from "./product-type";
import UsageWallet, { type UsageWalletTypeEnum } from "./usage-wallet";

class Product {
	public static EmptyConstructorParams: IProductConstructorParams = {
		description: "",
		discountPrice: 0,
		id: null,
		name: "",
		price: 0,
		type: "",
	};

	public static IsPlan(product: Product): boolean {
		return !!(
			product.family === ProductFamilyEnum.CORE &&
			product.type &&
			ProductTypeEnum.PLAN
		);
	}

	public static IsGrandfathered(
		availableProducts: Array<Product>,
		product: Product,
	): boolean {
		return !availableProducts.some((p) => p.id === product.id);
	}

	public static IsAddon(product: Product): boolean {
		return product.type === ProductTypeEnum.ADDON;
	}

	public static IsIDDAddon(product: Product): boolean {
		return Product.IsAddon(product) && product.family === ProductFamilyEnum.IDD;
	}

	public static IsIntlRoamingBooster(product: Product): boolean {
		return (
			product.type === ProductTypeEnum.BOOSTER &&
			product.family === ProductFamilyEnum.ROAMING
		);
	}

	public static IsBooster(product: Product): boolean {
		return product.type === ProductTypeEnum.BOOSTER;
	}

	public static ConstructEmpty(): Product {
		return Product.Construct(Product.EmptyConstructorParams);
	}

	responseData: IProductConstructorParams;

	public static ConstructList(
		arrParams: Array<IProductConstructorParams>,
	): Array<Product> {
		return map(Product.Construct)(arrParams);
	}

	public static Construct(params: IProductConstructorParams): Product {
		return new Product(params);
	}

	constructor(params: IProductConstructorParams) {
		this.responseData = params;
	}

	get id(): ID {
		if (this.responseData?.id) {
			return this.responseData.id;
		}

		if (this.responseData?.productId) {
			return this.responseData.productId;
		}

		return null;
	}

	get name(): string {
		return this.responseData.name;
	}

	get text(): string {
		const res = this.responseData.appUserData?.text;

		if (!res) {
			return "";
		}

		return res;
	}

	get textOverseas(): string {
		const res = this.responseData.appUserData?.textOverseas;

		if (!res) {
			return "";
		}

		return res;
	}

	get type(): ProductTypeEnum {
		if (!this.responseData.type) {
			return ProductTypeEnum.NONE;
		}

		const exists = mapStringProductType[this.responseData.type] !== undefined;

		if (!exists) {
			return ProductTypeEnum.NONE;
		}

		return mapStringProductType[this.responseData.type];
	}

	/**
	 * returns the raw type string as defined by the backend
	 */
	get rawType(): string {
		if (!this.responseData.type) {
			return "";
		}

		return this.responseData.type;
	}

	get family(): ProductFamilyEnum {
		if (!this.responseData.family) {
			return ProductFamilyEnum.NONE;
		}

		const exists =
			mapStringProductFamily[this.responseData.family] !== undefined;

		if (!exists) {
			return ProductFamilyEnum.NONE;
		}

		return mapStringProductFamily[this.responseData.family];
	}

	get validForDays(): number {
		if (this.responseData.validForDays === undefined) {
			return 0;
		}

		return this.responseData.validForDays;
	}

	get iconRef(): string {
		if (
			!this.responseData.appUserData ||
			!this.responseData.appUserData.iconRef
		) {
			return "";
		}

		return this.responseData.appUserData.iconRef;
	}

	get imageRef(): string | null {
		if (
			!this.responseData.appUserData ||
			!this.responseData.appUserData.imageRef
		) {
			return null;
		}

		return this.responseData.appUserData.imageRef;
	}

	get imageURL(): string | null {
		if (
			!this.responseData.appUserData ||
			!this.responseData.appUserData.imageURL
		) {
			return null;
		}

		return this.responseData.appUserData.imageURL;
	}

	get price(): number {
		return this.responseData.price;
	}

	get items(): Array<ProductItem> {
		if (
			this.responseData &&
			this.responseData.appUserData &&
			this.responseData.appUserData.productInclusions
		) {
			return ProductItem.ConstructList(
				this.responseData.appUserData.productInclusions,
			);
		}

		return [];
	}

	get extraInclusions(): Array<string> {
		if (!this.responseData?.appUserData?.extraInclusions) {
			return [];
		}

		return this.responseData.appUserData.extraInclusions;
	}

	get usageData(): Array<UsageWallet> {
		if (!this.responseData.wallets) {
			return [];
		}

		return this.responseData.wallets.map(UsageWallet.Construct).sort((a, b) => {
			if (a.order > b.order) return 1;
			if (b.order > a.order) return -1;
			return 0;
		});
	}

	usageAllowanceRemaining(type: UsageWalletTypeEnum): string | number {
		const wallet: UsageWallet | undefined = this.usageData.find(
			(el) => el.walletType === type,
		);

		if (!wallet) {
			return 0;
		}

		return wallet.data;
	}

	get hasExpiry(): boolean {
		return !!this.expiry;
	}

	get expiry(): Moment | null {
		if (this.usageData.length === 0) {
			return null;
		}

		return this.usageData[0].expiry;
	}

	get info(): ProductInfo | null {
		if (!this.responseData.appUserData?.info) {
			return null;
		}

		return ProductInfo.Construct(this.responseData.appUserData.info);
	}

	get productContinue(): boolean {
		return !!this.responseData.productContinue;
	}
}

export default Product;
